import type { ReactNode } from "react";
import { Link } from "react-router-dom";

type Variant = "primary" | "dark" | "outline" | "light" | "ghost";

interface CTAButtonProps {
  to?: string;
  href?: string;
  children: ReactNode;
  variant?: Variant;
  icon?: string;
  className?: string;
  onClick?: () => void;
}

const base =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-6 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer select-none";

const variants: Record<Variant, string> = {
  primary:
    "bg-primary-500 text-foreground-950 hover:bg-primary-600 hover:-translate-y-0.5",
  dark: "bg-foreground-950 text-background-50 hover:bg-foreground-800 hover:-translate-y-0.5",
  outline:
    "border border-foreground-300 text-foreground-900 hover:border-primary-500 hover:text-primary-600 bg-transparent",
  light:
    "bg-background-50 text-foreground-950 hover:bg-background-100 hover:-translate-y-0.5",
  ghost: "text-foreground-800 hover:text-primary-600 bg-transparent px-2",
};

export default function CTAButton({
  to,
  href,
  children,
  variant = "primary",
  icon,
  className = "",
  onClick,
}: CTAButtonProps) {
  const classes = `${base} ${variants[variant]} ${className}`;

  const content = (
    <>
      {children}
      {icon && <i className={`${icon} text-base`}></i>}
    </>
  );

  if (href) {
    return (
      <a href={href} className={classes} onClick={onClick} target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? "noreferrer" : undefined}>
        {content}
      </a>
    );
  }

  if (to) {
    return (
      <Link to={to} className={classes} onClick={onClick}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className={classes} onClick={onClick}>
      {content}
    </button>
  );
}